/**
 * PF Revamp — v2 session payment intent (closes ESC-W4-01).
 *
 * `POST|PUT /workspace/{workspaceId}/sessions/{sessionId}/payment-intent`.
 * Replaces the legacy `useCreateUpdatePFIntent` wrap (`/order/paymentIntent`),
 * whose `products: ProductQuantityDTO[]` wanted numeric legacy product ids the
 * v2 cart has never carried — the gap that made card and SEPA unreachable in
 * PF v2. The v2 contract takes `productCode` lines, so the cart can finally
 * price itself.
 *
 * WHAT THE INTENT CHARGES is `buildPaymentIntentBody`'s business, not this
 * hook's: the BE sums the lines it is given and derives no fees, so that
 * builder owns the "every chargeable line the order will bill" rule. Read its
 * header before changing anything about the body.
 *
 * Choreography this hook implements, all of it forced by the endpoint:
 *   - **create once, update after.** POST answers 201, PUT 200; a second POST
 *     on a session that already holds an intent is a **409**.
 *   - **409 on create ⇒ update.** A page reload loses this hook's memory of the
 *     intent while the session keeps it, so the first sync after a refresh would
 *     otherwise 409 and kill checkout. The recovery is not optional.
 *   - **409 "already placed an order" is a different conflict.** Both verbs
 *     refuse once the session has its order (a declined card leaves it placed
 *     and unpaid). It is never retried as an update; it surfaces as
 *     `isOrderAlreadyPlaced` and the intent already held stays usable, because
 *     it is the one the placed order was priced with.
 *   - **client only.** `role = ONLY_CLIENT` on both verbs. The BE already
 *     withholds card/SEPA from the admin instruction options, so an admin never
 *     reaches here; the workspace still resolves admin-first to match
 *     `useRevampPlaceOrder` rather than inventing a second convention.
 *
 * Every refusal is a rejected promise AND a `logger.warn`: a silent no-op on a
 * money path reads as "the button does nothing".
 */

import { useCallback, useMemo, useRef } from 'react'
import { useMutation } from '@tanstack/react-query'

import { logger } from 'utils/logging'
import { useAPIV2 } from 'utils/API'

import type { MeasurementOnSiteRequestDTO } from 'models/purchaseFlow/measurementDTO'
import type {
  CreatePaymentIntentV2DTO,
  MonetaryValueV2,
  PaymentIntentMethodV2,
  PaymentIntentProductV2DTO,
  PaymentIntentV2DTO,
  ResolvedCatalogue,
  UpdatePaymentIntentV2DTO
} from 'models/purchaseFlowRevamp'
import { usePfCatalogue } from 'dataQueries/purchaseFlowRevamp.query'

import { usePfStore } from 'components/pages/PurchaseFlowRevamp/store'
import { buildPaymentIntentBody } from 'components/pages/PurchaseFlowRevamp/store/serializePaymentIntent'
import { orderLevelConfigs } from 'components/pages/PurchaseFlowRevamp/store/serializeLineConfiguration'
import { buildProduct } from 'components/pages/PurchaseFlowRevamp/store/serializeLine'
import type { PfStoreState } from 'components/pages/PurchaseFlowRevamp/store'
import { useDefaultInvoicingAddress } from 'components/pages/PurchaseFlowRevamp/steps/billing/useDefaultInvoicingAddress'
import { useEffectivePaymentMethod } from 'components/pages/PurchaseFlowRevamp/steps/billing/useEffectivePaymentMethod'
import { usePfSessionWorkspaceId } from 'components/pages/PurchaseFlowRevamp/routing/usePfSessionWorkspaceId'

export type { PaymentIntentV2DTO }

enum Endpoints {
  /** POST (201) / PUT (200) — the session's Stripe intent. */
  SESSION_PAYMENT_INTENT = '/workspace/{workspaceId}/sessions/{sessionId}/payment-intent',
}

/** FE payment method → the wire's Stripe method. `invoice` has no mapping on
 *  purpose: an invoice order never opens an intent. */
const STRIPE_METHOD: Record<string, PaymentIntentMethodV2> = {
  card: 'CARD',
  sepa: 'SEPA_DEBIT',
}

/**
 * The cart's measurement configuration, taken from the very line builder placement uses, so the
 * amount Stripe authorises and the order that is billed are priced from one request.
 */
const measurementRequestOf = (
  state: PfStoreState,
  catalogue: ResolvedCatalogue | undefined
): MeasurementOnSiteRequestDTO | undefined => {
  const shared = orderLevelConfigs(state.productConfigs)
  for (const item of state.items) {
    const configuration = buildProduct(item, catalogue, state.attachments ?? {}, shared, state.productConfigs)?.configuration
    if (configuration?.type === 'MEASUREMENT_ON_SITE') {
      return configuration.request as unknown as MeasurementOnSiteRequestDTO
    }
  }
  return undefined
}

/** Raised when the session has already placed its order: neither verb can re-price it. */
export class PaymentIntentOrderPlacedError extends Error {
  constructor(readonly cause: unknown) {
    super('useRevampPaymentIntent: the session already placed an order')
  }
}

/** The BE answers 409 for an existing intent AND for a placed order; only its message tells them apart. */
const isOrderPlacedConflict = (error: unknown): boolean => {
  const response = (error as { response?: { status?: number, data?: { message?: unknown } } })?.response
  return response?.status === 409 && typeof response.data?.message === 'string' && /already placed an order/i.test(response.data.message)
}

/** Everything the request needs, resolved from state in one place so the create
 *  and update branches cannot disagree about what they are pricing. */
interface IntentInputs {
  workspaceId: string
  sessionId: string
  method: PaymentIntentMethodV2
  invoicingAddressId: string
  products: PaymentIntentProductV2DTO[]
  measurementOnSiteRequest?: MeasurementOnSiteRequestDTO
}

export interface UseRevampPaymentIntentResult {
  /**
   * Create the session's intent, or update it if one already exists — the
   * caller does not have to know which. Resolves with the intent (its
   * `clientSecret` is what Stripe confirms with, its `amount` is what will
   * actually be charged); rejects when the state cannot produce a valid request.
   */
  syncIntent: () => Promise<PaymentIntentV2DTO>
  isPending: boolean
  isError: boolean
  /** The last sync was refused because the session already placed its order. */
  isOrderAlreadyPlaced: boolean
  error: unknown
  /** Client secret of the latest intent, if one has been opened. */
  clientSecret: string | undefined
  /** GROSS amount the intent charges (VAT applied, discount deducted). Exceeds
   *  the cart's displayed net total by VAT — that gap is correct. */
  amount: MonetaryValueV2 | undefined
  /**
   * Stable identity of what the intent would charge for, right now: the lines,
   * the payment method and the invoicing address (VAT). `null` when the state
   * cannot produce a request at all.
   *
   * A caller resyncs when this changes — the intent is created once and then has
   * to keep up with the cart, or the client confirms a stale amount. Reactive on
   * purpose, unlike the rest of the hook, which reads `getState()` at call time.
   */
  intentKey: string | null
  reset: () => void
}

export function useRevampPaymentIntent(): UseRevampPaymentIntentResult {
  const api = useAPIV2<Endpoints>()

  // Subscribed, not read from `getState`: these are what the amount depends on,
  // so a change in any of them has to reach `intentKey` and trigger a resync.
  const items = usePfStore((s) => s.items)
  const extrasFromInstructions = usePfStore((s) => s.extrasFromInstructions)
  const paymentMethod = useEffectivePaymentMethod()
  const productConfigs = usePfStore((s) => s.productConfigs)
  // Billing highlights a sole saved address without writing it; the gates and placement accept it.
  const defaultInvoicingAddressId = useDefaultInvoicingAddress()?.id
  const invoicingAddressId = usePfStore((s) => s.invoicingAddress?.id) ?? defaultInvoicingAddressId

  const catalogue = usePfCatalogue().data

  const workspaceId = usePfSessionWorkspaceId()

  /** Whether THIS hook has opened an intent. The session may hold one we never
   *  created (a reload), which is what the 409 recovery is for. */
  const hasIntentRef = useRef(false)
  const intentRef = useRef<PaymentIntentV2DTO | undefined>(undefined)

  const resolveInputs = useCallback((): IntentInputs | null => {
    const state = usePfStore.getState()
    const report = (reason: string, metadata?: Record<string, unknown>) => {
      logger.warn(`useRevampPaymentIntent: ${reason} — no payment intent requested`, undefined, {
        context: { component: 'useRevampPaymentIntent', action: 'resolveInputs', metadata },
      })
      return null
    }

    if (!workspaceId) return report('workspace unresolved')
    if (!state.draft.sessionId) return report('sessionId missing from the store')

    const method = paymentMethod ? STRIPE_METHOD[paymentMethod] : undefined
    if (!method) return report('payment method is not a Stripe one', { paymentMethod })

    // Wire-required, and the BE derives the VAT rate from it.
    const invoicingAddressId = state.invoicingAddress?.id ?? defaultInvoicingAddressId
    if (!invoicingAddressId) return report('no invoicing address selected')

    const products = buildPaymentIntentBody(state, catalogue)
    if (!products) return report('cart resolves to no chargeable line')

    return {
      workspaceId,
      sessionId: state.draft.sessionId,
      method,
      invoicingAddressId,
      products,
      measurementOnSiteRequest: measurementRequestOf(state, catalogue),
    }
  }, [catalogue, workspaceId, paymentMethod, defaultInvoicingAddressId])

  const intentKey = useMemo(() => {
    const method = paymentMethod ? STRIPE_METHOD[paymentMethod] : undefined
    if (!method || !invoicingAddressId) return null
    // Built through the same serializer the request uses, so the key cannot
    // describe a different body than the one that would be sent. `items` and
    // `extrasFromInstructions` are in the dep list above; the serializer needs
    // the whole state, hence the read here.
    const state = usePfStore.getState()
    const products = buildPaymentIntentBody(state, catalogue)
    if (!products) return null
    return JSON.stringify({ products, method, invoicingAddressId, measurementOnSiteRequest: measurementRequestOf(state, catalogue) })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalogue, items, extrasFromInstructions, productConfigs, paymentMethod, invoicingAddressId])

  const mutation = useMutation<PaymentIntentV2DTO, unknown, IntentInputs>({
    mutationFn: async ({ workspaceId: ws, sessionId, method, invoicingAddressId, products, measurementOnSiteRequest }) => {
      const replacements = { workspaceId: ws, sessionId }
      const measurement = measurementOnSiteRequest ? { measurementOnSiteRequest } : {}

      const refuseIfOrdered = (error: unknown): never => {
        if (!isOrderPlacedConflict(error)) throw error
        logger.warn('useRevampPaymentIntent: session already placed an order — the intent cannot be re-priced', undefined, {
          context: { component: 'useRevampPaymentIntent', action: 'orderAlreadyPlaced', metadata: { sessionId } },
        })
        throw new PaymentIntentOrderPlacedError(error)
      }

      const update = (): Promise<PaymentIntentV2DTO> => {
        // The update body takes a SINGULAR, nullable `paymentMethod` — not the
        // create body's `paymentMethodTypes` array.
        const body: UpdatePaymentIntentV2DTO = { products, paymentMethod: method, invoicingAddressId, ...measurement }
        return api.put<PaymentIntentV2DTO>(Endpoints.SESSION_PAYMENT_INTENT, replacements, body, false).catch(refuseIfOrdered)
      }

      if (hasIntentRef.current) return update()

      const body: CreatePaymentIntentV2DTO = { products, paymentMethodTypes: [method], invoicingAddressId, ...measurement }
      try {
        return await api.post<PaymentIntentV2DTO>(Endpoints.SESSION_PAYMENT_INTENT, replacements, body, false)
      } catch (error) {
        if (isOrderPlacedConflict(error)) return refuseIfOrdered(error)
        const status = (error as { response?: { status?: number } })?.response?.status
        // 409 = the SESSION already has an intent (we just did not open it —
        // typically a reload). Anything else is a real failure and must surface.
        if (status !== 409) throw error
        logger.warn('useRevampPaymentIntent: session already holds an intent — updating it instead', undefined, {
          context: { component: 'useRevampPaymentIntent', action: 'recoverFromConflict', metadata: { sessionId } },
        })
        return update()
      }
    },
    onSuccess: (intent) => {
      hasIntentRef.current = true
      intentRef.current = intent
    },
  })

  const syncIntent = useCallback(async (): Promise<PaymentIntentV2DTO> => {
    const inputs = resolveInputs()
    if (!inputs) throw new Error('useRevampPaymentIntent: cannot assemble a payment intent from current state')
    return mutation.mutateAsync(inputs)
  }, [mutation, resolveInputs])

  const reset = useCallback(() => {
    hasIntentRef.current = false
    intentRef.current = undefined
    mutation.reset()
  }, [mutation])

  const intent = mutation.data ?? intentRef.current

  return useMemo(
    () => ({
      syncIntent,
      isPending: mutation.isPending,
      isError: mutation.isError,
      isOrderAlreadyPlaced: mutation.error instanceof PaymentIntentOrderPlacedError,
      error: mutation.error,
      clientSecret: intent?.clientSecret,
      amount: intent?.amount,
      intentKey,
      reset,
    }),
    [intent?.amount, intent?.clientSecret, intentKey, mutation.error, mutation.isError, mutation.isPending, reset, syncIntent]
  )
}
